import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, Clock, GraduationCap, Trophy, Users, Target, CheckCircle, BookOpen, Certificate, Buildings, ChartLineUp, Sparkle } from "@phosphor-icons/react";
import SblChatWidget from "../components/SblChatWidget";
import { APPLY_GOOGLE_FORM_URL } from "../lib/applyLinks";
import { isEnrolling } from "../lib/programmeStatus";

const SBL_LOGO = "/sbl-logo.jpeg";

/**
 * Public programme marketing page — auto-rendered per programme.
 * URL: /p/sbl-p01  (or /p/{id})
 */

// Hoisted out of the page so React keeps the same component identity across
// re-renders (avoids the `no-unstable-nested-components` rule and prevents
// children from re-mounting unnecessarily).
function ApplyCTA({ prog, testid, label, className }) {
  const enrollmentOpen = isEnrolling(prog);
  const isFree = !prog?.price_usd;
  if (!enrollmentOpen) {
    return (
      <Link to="/#contact">
        <Button className={className} data-testid={testid}>Make Enquiry <ArrowRight weight="bold" size={16} /></Button>
      </Link>
    );
  }
  if (isFree) {
    return (
      <Link to={`/register?mode=programme&programme=${prog.id}`}>
        <Button className={className} data-testid={testid}>{label} <ArrowRight weight="bold" size={16} /></Button>
      </Link>
    );
  }
  return (
    <a href={APPLY_GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer">
      <Button className={className} data-testid={testid}>{label} <ArrowRight weight="bold" size={16} /></Button>
    </a>
  );
}

export default function PublicProgrammePage() {
  const { idOrCode } = useParams();
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  // Section 7 — Universal sections applied to every public programme page.
  const [universal, setUniversal] = useState({});

  // Button contract (Round 30 — single source of truth: programme.status):
  //   • status === 'active' and programme is paid → Apply Now → Google Form (new tab)
  //   • status === 'active' and programme is free → Apply Now → /register (platform signup)
  //   • otherwise (inactive / draft / unknown) → Make Enquiry → landing-page contact section
  const enrollmentOpen = isEnrolling(prog);

  useEffect(() => {
    setLoading(true);
    // Public surface — use /public-programmes so admin-created LMS offerings
    // don't accidentally resolve when a visitor lands on /p/{code}.
    api.get("/public-programmes").then(r => {
      const all = r.data || [];
      const match = all.find(p =>
        p.id === idOrCode ||
        (p.code || "").toLowerCase() === (idOrCode || "").toLowerCase()
      );
      setProg(match || null);
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
    api.get("/cms/universal-sections").then(r => setUniversal(r.data || {})).catch(() => {});
  }, [idOrCode]);

  useEffect(() => {
    if (prog?.id) {
      api.get(`/programmes/${prog.id}/courses`).then(r => setCourses(r.data || [])).catch(() => setCourses([]));
    }
  }, [prog?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading programme…</div>;
  if (!prog) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <p className="text-base text-slate-700 mb-4">Programme not found.</p>
      <Link to="/programmes-catalogue"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Back to all programmes</Button></Link>
    </div>
  );

  const isFlagship = (prog.code || "").endsWith("P22") || (prog.level || "").toLowerCase().includes("fellowship");
  const isAdvanced = (prog.level || "").toLowerCase().includes("advanced");
  const isFoundation = (prog.level || "").toLowerCase().includes("foundation");
  const careerOutcomes = Array.isArray(prog.ideal_for)
  ? prog.ideal_for
  : (typeof prog.ideal_for === 'string' && prog.ideal_for.trim()
    ? prog.ideal_for.split(',').map(s => s.trim()).filter(Boolean)
    : []);

  // Round 31 (Issue 8) — Universal CMS sections, when populated, override
  // the hardcoded fallback copy. Admins edit /admin#universal once and the
  // change cascades to every programme page (PublicProgrammePage,
  // ProgrammesCataloguePage, etc.). Empty/whitespace strings fall back.
  const cmsList = (key, fallback) => {
    const v = (universal[key] || "").trim();
    if (!v) return fallback;
    return v.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  };

  const learningObjectives = cmsList("learning_objectives", [
    "Master the practical systems and frameworks that define excellence in this domain",
    "Apply globally-recognised standards to your day-to-day school operations",
    "Build measurable competence assessed through capstone projects and peer review",
    "Earn an NQF-aligned credential recognised across African institutions",
    "Join an active community of peers leading the transformation of African schools",
  ]);

  const admissionRequirements = cmsList("admission_requirements", isFoundation ? [
    "No prior qualifications required",
    "Currently working or aspiring to work in a school environment",
    "Commitment to attend live sessions and complete coursework",
  ] : isAdvanced ? [
    "Minimum 3 years professional experience in school leadership or operations",
    "Bachelor's degree OR equivalent professional certification",
    "Letter of intent + current CV",
    "Reference from current/former employer",
  ] : [
    "Working knowledge of school operations or administration",
    "Bachelor's degree, ND/HND, or 2+ years relevant experience",
    "Computer literacy and reliable internet access",
  ]);

  const deliveryStructure = [
    { icon: BookOpen, label: "Self-paced lessons", desc: "Video, slides, downloadable readings" },
    { icon: Users, label: "Live cohort sessions", desc: "Weekly Q&A with instructors" },
    { icon: Target, label: "Module quizzes", desc: "Reinforce learning, unlock next module" },
    { icon: Trophy, label: "Capstone project", desc: "Apply learning to your real school" },
    { icon: Certificate, label: "Credential", desc: `${prog.level || "Professional Certificate"} · ${prog.nqf_level || ""}` },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`programme-public-${prog.code || prog.id}`}>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span>
          </Link>
          <Link to="/programmes-catalogue" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={14} /> All programmes</Link>
        </div>
      </header>

      <section className={`py-16 sm:py-20 text-white relative overflow-hidden ${isFlagship ? "bg-gradient-to-br from-slate-950 via-amber-950 to-amber-900" : isAdvanced ? "bg-gradient-to-br from-slate-950 to-blue-950" : isFoundation ? "bg-gradient-to-br from-emerald-900 to-slate-900" : "bg-gradient-to-br from-blue-900 to-slate-950"}`}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {prog.code && <span className="bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-widest px-3 py-1">{prog.code}</span>}
            <span className="bg-white/10 border border-white/20 text-xs px-3 py-1">{prog.level || "Professional Certificate"}</span>
            {prog.nqf_level && <span className="bg-white/10 border border-white/20 text-xs px-3 py-1">{prog.nqf_level}</span>}
            {isFlagship && <span className="bg-amber-500 text-slate-950 text-xs font-bold uppercase px-3 py-1 flex items-center gap-1"><Sparkle weight="fill" size={12} /> Flagship</span>}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>{prog.title}</h1>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl mb-6">{prog.overview || "A comprehensive professional development pathway designed for school leaders ready to elevate their institutions."}</p>

          {/* Programme price */}
          {prog.price_usd && (
            <div className="inline-block bg-white/10 border border-amber-300/30 px-5 py-3 mb-6 backdrop-blur-sm" data-testid="programme-price-hero">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-1">Programme Fee</p>
              <p className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                ${prog.price_usd}
                {exchangeRate && <span className="text-base sm:text-lg text-white/70 font-normal ml-2">≈ ₦{Math.round(prog.price_usd * exchangeRate).toLocaleString()}</span>}
              </p>
              <p className="text-xs text-white/60 mt-1">Minimum 60% upfront · Balance due within 6 weeks of programme start</p>
            </div>
          )}

          <div className="flex gap-3 flex-wrap mb-6">
            <ApplyCTA prog={prog} testid="programme-apply-cta" label="Apply Now" className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-none gap-2 px-6 py-6 font-bold" />
            <a href="#structure">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-slate-950 rounded-none px-6 py-6 bg-transparent">View Structure</Button>
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mt-8">
            {[
              { icon: Clock, label: "Duration", value: prog.duration || "Flexible" },
              { icon: GraduationCap, label: "Level", value: prog.nqf_level || "NQF" },
              { icon: Users, label: "Cohort", value: "Live + Self-Paced" },
              { icon: Certificate, label: "Credential", value: "NQF Aligned" },
            ].map(st => {
              const Icon = st.icon;
              return (
                <div key={st.label} className="bg-white/5 border border-white/15 p-3">
                  <Icon size={16} className="text-amber-300 mb-1" />
                  <p className="text-[10px] uppercase tracking-wider text-white/60">{st.label}</p>
                  <p className="text-sm font-bold">{st.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Learning Objectives</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>By the end of this programme you'll be able to…</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {learningObjectives.map((obj, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200">
                <div className="bg-emerald-100 text-emerald-700 w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">{i + 1}</div>
                <p className="text-sm text-slate-700 leading-relaxed">{obj}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Ideal For · Career Outcomes</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>This programme is built for…</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {careerOutcomes.length > 0 ? careerOutcomes.map(c => (
              <div key={c} className="bg-white border border-slate-200 p-4 hover:border-blue-800 hover:shadow-md transition-all">
                <Buildings size={20} weight="fill" className="text-blue-800 mb-2" />
                <p className="text-sm font-semibold text-slate-950">{c}</p>
              </div>
            )) : <p className="text-sm text-slate-500 col-span-full">Multiple career paths in school leadership and administration.</p>}
          </div>
        </div>
      </section>

      {/* Programme Structure — CMS copy when set, otherwise the original
          icon grid. Single source of truth: /admin#universal. */}
      <section id="structure" className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Programme Structure</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>How you'll learn</h2>
          {(universal.programme_structure || "").trim() ? (
            <ul className="space-y-2 max-w-3xl" data-testid="universal-programme_structure">
              {(universal.programme_structure || "").split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-slate-50 border-l-4 border-amber-500">
                  <Target size={18} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {deliveryStructure.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="bg-slate-50 border border-slate-200 p-4 text-center">
                    <Icon size={28} weight="fill" className="mx-auto text-amber-600 mb-2" />
                    <p className="text-sm font-bold text-slate-950 mb-1">{s.label}</p>
                    <p className="text-xs text-slate-600">{s.desc}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {courses.length > 0 && (
        <section className="py-16 bg-slate-50 border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Courses In This Programme</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>{courses.length} course{courses.length === 1 ? "" : "s"} you'll work through</h2>
            <div className="space-y-2">
              {courses.map((c, i) => (
                <div key={c.id} className="bg-white border border-slate-200 px-4 py-3 flex items-center gap-3 hover:border-blue-800 transition-all">
                  <div className="bg-blue-800 text-white w-8 h-8 flex items-center justify-center font-bold text-xs flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-950 truncate">{c.title}</p>
                    <p className="text-xs text-slate-500">{(c.modules || []).length} modules</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Expectations — CMS-driven list when set. */}
      <section className="py-16 bg-white border-b border-slate-200" id="expectations">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Expectations</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>What is expected of you</h2>
          {(universal.expectations || "").trim() ? (
            <ul className="space-y-2 max-w-3xl" data-testid="universal-expectations">
              {(universal.expectations || "").split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-slate-50 border-l-4 border-amber-500">
                  <Clock size={18} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Clock, title: "Time commitment", body: `Plan 6–8 hours each week — typically 1 live class, self-paced video lessons, a forum discussion, and a short assessment.` },
                { icon: Target, title: "Assessments", body: "Every module ends in a quiz that must be passed before the next module unlocks. Quizzes pause and resume; you can revisit lessons before re-attempting." },
                { icon: Trophy, title: "Capstone project", body: "Mid-way through the programme you'll begin a capstone — a real plan you can apply at your school. Instructor reviews it and contributes 30% of your final grade." },
              ].map(b => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="bg-slate-50 border border-slate-200 p-5">
                    <div className="bg-amber-400 text-slate-950 w-9 h-9 flex items-center justify-center mb-3"><Icon size={18} weight="bold" /></div>
                    <p className="text-sm font-bold text-slate-950 mb-1">{b.title}</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Programme Timeline — CMS-driven phases when set. */}
      <section className="py-16 bg-slate-50 border-b border-slate-200" id="timeline">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Programme Timeline</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Your journey, phase by phase</h2>
          <p className="text-sm text-slate-600 mb-8 max-w-2xl">Built around the {prog.duration || "programme duration"} cohort. Each phase has clear outcomes you can show your team and your board.</p>
          {(universal.programme_timeline || "").trim() ? (
            <ol className="relative border-l-2 border-amber-300 pl-6 space-y-4" data-testid="universal-programme_timeline">
              {(universal.programme_timeline || "").split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="relative">
                  <div className="absolute -left-[34px] top-0 w-6 h-6 bg-amber-400 text-slate-950 flex items-center justify-center text-[10px] font-bold rounded-full ring-4 ring-white">{i + 1}</div>
                  <div className="bg-white border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed">{line}</div>
                </li>
              ))}
            </ol>
          ) : (
            <ol className="relative border-l-2 border-amber-300 pl-6 space-y-6">
              {[
                { phase: "Phase 1 — Foundation", weeks: "Weeks 1–4", desc: "Onboarding, peer introductions, the leadership framework, baseline diagnostic of your school." },
                { phase: "Phase 2 — Application", weeks: "Weeks 5–10", desc: "Apply the systems in your school: case clinics, role-play, weekly accountability check-ins." },
                { phase: "Phase 3 — Mastery", weeks: "Weeks 11–16", desc: "Deep-dive specialist tracks, advanced electives, peer-led roundtables and field visits." },
                { phase: "Phase 4 — Capstone & Graduation", weeks: "Final weeks", desc: "Present your capstone to the SBL faculty and your cohort. Receive your NQF-aligned credential." },
              ].map((p, i) => (
                <li key={i} className="relative">
                  <div className="absolute -left-[34px] top-0 w-6 h-6 bg-amber-400 text-slate-950 flex items-center justify-center text-[10px] font-bold rounded-full ring-4 ring-white">{i + 1}</div>
                  <div className="bg-white border border-slate-200 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                      <p className="text-sm font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.phase}</p>
                      <span className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{p.weeks}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Learning Journey — CMS-driven steps when set; SVG flowchart fallback. */}
      <section className="py-16 bg-white border-b border-slate-200" id="journey">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Learning Journey</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>From application to credential</h2>
          <p className="text-sm text-slate-600 mb-8 max-w-2xl">A visual map of how you'll progress through this programme — every milestone is graded and unlocks the next step.</p>
          {(universal.learning_journey || "").trim() ? (
            <ol className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none" data-testid="universal-learning_journey">
              {(universal.learning_journey || "").split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="bg-slate-50 border-l-4 border-blue-800 p-4 flex items-start gap-3">
                  <span className="bg-blue-800 text-white w-7 h-7 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="bg-slate-50 border border-slate-200 p-4 sm:p-6 overflow-x-auto">
              <svg viewBox="0 0 880 260" className="w-full min-w-[760px]" data-testid="programme-journey-svg" preserveAspectRatio="xMidYMid meet">
                <line x1="60" y1="130" x2="820" y2="130" stroke="#fbbf24" strokeWidth="3" strokeDasharray="6 6" />
                {[
                  { x: 60,  label: "Apply",       sub: "Submit application",     fill: "#1e3a8a", text: "#ffffff" },
                  { x: 215, label: "Foundation",  sub: "Weeks 1–4",              fill: "#fbbf24", text: "#0f172a" },
                  { x: 370, label: "Application", sub: "Weeks 5–10",             fill: "#0f766e", text: "#ffffff" },
                  { x: 525, label: "Mastery",     sub: "Weeks 11–16",            fill: "#7c3aed", text: "#ffffff" },
                  { x: 680, label: "Capstone",    sub: "Final project",          fill: "#be123c", text: "#ffffff" },
                  { x: 820, label: "Credential",  sub: prog.nqf_level || "NQF",  fill: "#0f172a", text: "#fbbf24" },
                ].map((n, i) => (
                  <g key={i}>
                    <circle cx={n.x} cy="130" r="36" fill={n.fill} stroke="#ffffff" strokeWidth="3" />
                    <text x={n.x} y="135" textAnchor="middle" fill={n.text} fontSize="13" fontWeight="700" fontFamily="Outfit, sans-serif">{i + 1}</text>
                    <text x={n.x} y="195" textAnchor="middle" fill="#0f172a" fontSize="13" fontWeight="700" fontFamily="Outfit, sans-serif">{n.label}</text>
                    <text x={n.x} y="215" textAnchor="middle" fill="#64748b" fontSize="11">{n.sub}</text>
                    <text x={n.x} y="70"  textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="2">STEP {i + 1}</text>
                  </g>
                ))}
              </svg>
            </div>
          )}
        </div>
      </section>

      {/* Weekly Activities — CMS-driven cards when set; default day-by-day fallback. */}
      <section className="py-16 bg-white border-b border-slate-200" id="weekly-activities">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Weekly Activities</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>A week inside the programme</h2>
          <p className="text-sm text-slate-600 mb-8 max-w-2xl">Every week follows the same rhythm so you always know what's next.</p>
          {(universal.weekly_activities || "").trim() ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="universal-weekly_activities">
              {(universal.weekly_activities || "").split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="bg-slate-50 border-l-4 border-amber-500 p-4 flex items-start gap-3">
                  <span className="bg-amber-400 text-slate-950 w-7 h-7 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {[
                { day: "Mon", title: "Live class", color: "bg-amber-400 text-slate-950" },
                { day: "Tue–Wed", title: "Self-paced lessons", color: "bg-blue-800 text-white" },
                { day: "Thu", title: "Forum discussion", color: "bg-emerald-700 text-white" },
                { day: "Fri", title: "Module quiz", color: "bg-rose-700 text-white" },
                { day: "Sat", title: "Reflection & peer review", color: "bg-slate-900 text-white" },
              ].map((d, i) => (
                <div key={d.title} className="flex items-center md:contents">
                  <div className="flex-1 md:flex-none">
                    <div className={`${d.color} px-3 py-3 text-center`}>
                      <p className="text-[10px] uppercase tracking-widest opacity-80 mb-0.5">{d.day}</p>
                      <p className="text-sm font-bold">{d.title}</p>
                    </div>
                  </div>
                  {i < 4 && (
                    <div className="text-amber-500 px-1 md:py-3 md:hidden">
                      <ArrowRight size={18} weight="bold" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-4 italic">Sundays are intentionally free for rest, family, and your school.</p>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Admission Requirements</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>What you need to apply</h2>
          <div className="space-y-2 max-w-2xl">
            {admissionRequirements.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 border-l-4 border-blue-800">
                <CheckCircle size={20} weight="fill" className="text-blue-800 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{r}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-800 font-bold mb-2">Frequently Asked</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>Common questions</h2>

          {/* Section 7 — Universal sections from CMS override the default copy
              when populated. Admin edits cascade to every programme page. */}
          {universal.faq ? (
            <div className="bg-white border border-slate-200 p-5 whitespace-pre-line text-sm text-slate-700 leading-relaxed" data-testid="universal-faq">{universal.faq}</div>
          ) : (
            <div className="space-y-3">
              {[
                { q: "When is the next intake?", a: "Programmes run in rolling cohorts. Apply now to be assigned to the next available cohort." },
                { q: "How long will I have access?", a: `${prog.duration || "Programme duration"} of guided cohort learning + 12 months self-paced revision access after graduation.` },
                { q: "Is the certificate recognised?", a: `Yes. All SBL certificates are aligned to ${prog.nqf_level || "NQF Levels 4–8"} and include a verifiable digital credential.` },
                { q: "What payment options are available?", a: "Bank transfer in NGN or USD. Pay a minimum of 60% upfront to lock your place; the outstanding balance is due within 6 weeks of the programme start date. Access to course materials will be suspended automatically if the balance is not cleared by the deadline." },
                { q: "Can I cancel after payment?", a: "Cancellations are accepted up to 14 days before the programme start date for a full refund minus a 10% processing fee." },
              ].map((f, i) => (
                <details key={i} className="bg-white border border-slate-200 p-4 group" data-testid={`faq-${i}`}>
                  <summary className="font-semibold text-sm text-slate-950 cursor-pointer flex items-center justify-between">
                    <span>{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Round 31 (Issue 8) — duplicate universal-cross-sections block
          removed; each universal field is now rendered in-place above as a
          first-class override of its hardcoded fallback. */}

      <section className="py-16 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <ChartLineUp size={48} weight="fill" className="mx-auto text-amber-400 mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to apply?</h2>
          <p className="text-base text-white/80 mb-6">Join the next cohort of school leaders earning {prog.code || "SBL"} credentials.</p>
          <ApplyCTA
            prog={prog}
            testid="programme-final-cta"
            label={enrollmentOpen ? `Apply for ${prog.code || "this programme"}` : "Make Enquiry"}
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-none gap-2 px-8 py-6 text-base font-bold"
          />
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-xs">
          © {new Date().getFullYear()} School Builder Lab Africa. {prog.code} · {prog.title}
        </div>
      </footer>

      <SblChatWidget />
    </div>
  );
}
